import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button, Checkbox, Grid, Header, Icon, Segment } from "semantic-ui-react";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { downloadAllDocumentsAsPdf } from "../../../app/common/util/util";

export default function DocDownloadAllOptionsModal({
  baseDocs,
  folderGroups,
  archivedGroups,
  transaction,
  currentUserProfile,
}) {
  const dispatch = useDispatch();
  const [downloading, setDownloading] = useState(false);

  const [selectedFolders, setSelectedFolders] = useState(
    folderGroups.reduce((acc, group) => ({ ...acc, [group.label]: true }), {})
  );
  const [selectedArchived, setSelectedArchived] = useState(
    archivedGroups.reduce((acc, group) => ({ ...acc, [group.label]: true }), {})
  );

  function handleClose() {
    dispatch(closeModal({ modalType: "DocDownloadAllOptionsModal" }));
  }

  async function handleDownload() {
    const docs = [
      ...baseDocs,
      ...folderGroups
        .filter((group) => selectedFolders[group.label])
        .flatMap((group) => group.docs),
      ...archivedGroups
        .filter((group) => selectedArchived[group.label])
        .flatMap((group) => group.docs),
    ];

    if (docs.length === 0) {
      toast.error("No documents available to download");
      return;
    }

    setDownloading(true);
    try {
      const hasNonPdfDocs = docs.some(
        (doc) => doc.filetype && doc.filetype !== "pdf"
      );
      await downloadAllDocumentsAsPdf(docs, transaction, currentUserProfile);
      toast.success(
        hasNonPdfDocs
          ? "Documents downloaded as ZIP file successfully!"
          : "Documents downloaded as merged PDF successfully!"
      );
      handleClose();
    } catch (error) {
      console.error("Error downloading all documents:", error);
      toast.error(error.message || "Failed to download documents");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <ModalWrapper size="small">
      <Segment>
        <Header as="h2" color="blue" className="zero bottom margin">
          Download Documents
        </Header>
        <p className="small top margin">
          Choose which folders and archived sections to include in the
          download, in addition to the active and shared documents.
        </p>
        <Grid>
          {folderGroups.length > 0 && (
            <Grid.Column width={16}>
              <Header as="h4" className="small bottom margin">
                Folders
              </Header>
              {folderGroups.map((group) => (
                <div key={`folder-${group.label}`} className="tiny bottom margin">
                  <Checkbox
                    label={
                      <label>
                        <Icon name="folder" />
                        {group.label}
                      </label>
                    }
                    checked={!!selectedFolders[group.label]}
                    onChange={(e, { checked }) =>
                      setSelectedFolders((prev) => ({
                        ...prev,
                        [group.label]: checked,
                      }))
                    }
                  />
                </div>
              ))}
            </Grid.Column>
          )}
          {archivedGroups.length > 0 && (
            <Grid.Column width={16}>
              <Header as="h4" className="small bottom margin">
                Archived
              </Header>
              {archivedGroups.map((group) => (
                <div key={`archived-${group.label}`} className="tiny bottom margin">
                  <Checkbox
                    label={group.label === "Archived" ? "Archived" : `Archived - ${group.label}`}
                    checked={!!selectedArchived[group.label]}
                    onChange={(e, { checked }) =>
                      setSelectedArchived((prev) => ({
                        ...prev,
                        [group.label]: checked,
                      }))
                    }
                  />
                </div>
              ))}
            </Grid.Column>
          )}
        </Grid>
        <div className="medium top margin float-right">
          <Button disabled={downloading} onClick={handleClose} content="Cancel" />
          <Button
            primary
            loading={downloading}
            disabled={downloading}
            onClick={handleDownload}
            content="Download"
          />
        </div>
      </Segment>
    </ModalWrapper>
  );
}
